import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { competitionApi } from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  TrophyIcon,
  CalendarIcon,
  UserGroupIcon,
  UserIcon,
  AcademicCapIcon,
  PaintBrushIcon,
  MusicalNoteIcon
} from '@heroicons/react/24/outline'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'

const competitionSchema = z.object({
  name: z.string().min(2, 'Competition name must be at least 2 characters'),
  description: z.string().optional(),
  category: z.enum(['coptic_hymns', 'coptic_language', 'spiritual', 'art', 'sports']),
  type: z.enum(['individual', 'team']),
  sport_type: z.enum(['volleyball', 'soccer', 'chess', 'ping_pong']).optional(),
  age_group_id: z.number().min(1, 'Please select an age group'),
  max_participants: z.number().optional(),
  registration_deadline: z.string().min(1, 'Registration deadline is required'),
  start_date: z.string().min(1, 'Start date is required'),
  end_date: z.string().min(1, 'End date is required'),
  is_active: z.boolean().default(true)
})

type CompetitionForm = z.infer<typeof competitionSchema>

interface Competition {
  id: number
  name: string
  description: string
  category: 'coptic_hymns' | 'coptic_language' | 'spiritual' | 'art' | 'sports'
  type: 'individual' | 'team'
  sport_type?: 'volleyball' | 'soccer' | 'chess' | 'ping_pong'
  age_group_name: string
  age_group_id: number
  max_participants?: number
  registration_deadline: string
  start_date: string
  end_date: string
  is_active: boolean
  registered_count?: number
  created_at: string
}

const categoryIcons = {
  coptic_hymns: MusicalNoteIcon,
  coptic_language: AcademicCapIcon,
  spiritual: AcademicCapIcon,
  art: PaintBrushIcon,
  sports: TrophyIcon
}

const categoryNames = {
  coptic_hymns: 'Coptic Hymns',
  coptic_language: 'Coptic Language',
  spiritual: 'Spiritual',
  art: 'Art',
  sports: 'Sports'
}

const sportTypeNames = {
  volleyball: 'Volleyball',
  soccer: 'Soccer',
  chess: 'Chess',
  ping_pong: 'Ping Pong'
}

// Grade-based age groups
const ageGroups = [
  { id: 1, name: 'Angels' },
  { id: 2, name: '1st Grade' },
  { id: 3, name: '2nd Grade' },
  { id: 4, name: '3rd Grade' },
  { id: 5, name: '4th Grade' },
  { id: 6, name: '5th Grade' },
  { id: 7, name: '6th Grade' },
  { id: 8, name: '7th Grade' },
  { id: 9, name: '8th Grade' },
  { id: 10, name: '9th Grade' },
  { id: 11, name: '10th Grade' },
  { id: 12, name: '11th Grade' },
  { id: 13, name: '12th Grade' },
  { id: 14, name: 'College' },
  { id: 15, name: 'Adults' },
  { id: 16, name: 'Servants' }
]

const CompetitionManagement = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCompetition, setEditingCompetition] = useState<Competition | null>(null)
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [filterType, setFilterType] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  
  const queryClient = useQueryClient()

  const { data: competitions = [], isLoading, error } = useQuery(
    'admin-competitions',
    competitionApi.getCompetitions,
    {
      select: (response) => response.data
    }
  )

  const createMutation = useMutation(competitionApi.createCompetition, {
    onSuccess: () => {
      queryClient.invalidateQueries('admin-competitions')
      setIsModalOpen(false)
      reset()
      toast.success('Competition created successfully')
    },
    onError: (_error: any) => {
      toast.error((error as any)?.response?.data?.error || 'Failed to create competition')
    }
  })

  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: Partial<Competition> }) => 
      competitionApi.updateCompetition(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-competitions')
        setIsModalOpen(false)
        setEditingCompetition(null)
        reset()
        toast.success('Competition updated successfully')
      },
      onError: (_error: any) => {
        toast.error((error as any)?.response?.data?.error || 'Failed to update competition')
      }
    }
  )

  const deleteMutation = useMutation(competitionApi.deleteCompetition, {
    onSuccess: () => {
      queryClient.invalidateQueries('admin-competitions')
      toast.success('Competition deleted successfully')
    },
    onError: (_error: any) => {
      toast.error((error as any)?.response?.data?.error || 'Failed to delete competition')
    }
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm<CompetitionForm>({
    resolver: zodResolver(competitionSchema),
    defaultValues: {
      is_active: true,
      category: 'coptic_hymns',
      type: 'individual'
    }
  })

  const selectedCategory = watch('category')
  // const selectedType = watch('type') // For future type-specific logic

  const filteredCompetitions = competitions.filter((competition: Competition) => {
    const matchesSearch = competition.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         competition.description?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = filterCategory === 'all' || competition.category === filterCategory
    const matchesType = filterType === 'all' || competition.type === filterType
    return matchesSearch && matchesCategory && matchesType
  })

  const openCreateModal = () => {
    setEditingCompetition(null)
    reset({
      is_active: true,
      category: 'coptic_hymns',
      type: 'individual'
    })
    setIsModalOpen(true)
  }

  const openEditModal = (competition: Competition) => {
    setEditingCompetition(competition)
    setValue('name', competition.name)
    setValue('description', competition.description || '')
    setValue('category', competition.category)
    setValue('type', competition.type)
    setValue('sport_type', competition.sport_type)
    setValue('age_group_id', competition.age_group_id)
    setValue('max_participants', competition.max_participants)
    setValue('registration_deadline', competition.registration_deadline.split('T')[0])
    setValue('start_date', competition.start_date.split('T')[0])
    setValue('end_date', competition.end_date.split('T')[0])
    setValue('is_active', competition.is_active)
    setIsModalOpen(true)
  }

  const onSubmit = (data: CompetitionForm) => {
    const submissionData = {
      ...data,
      registration_deadline: new Date(data.registration_deadline).toISOString(),
      start_date: new Date(data.start_date).toISOString(),
      end_date: new Date(data.end_date).toISOString()
    }

    if (editingCompetition) {
      updateMutation.mutate({ id: editingCompetition.id, data: submissionData })
    } else {
      createMutation.mutate(submissionData)
    }
  }

  const handleDelete = (competitionId: number, competitionName: string) => {
    if (window.confirm(`Are you sure you want to delete "${competitionName}"? This action cannot be undone.`)) {
      deleteMutation.mutate(competitionId)
    }
  }

  const toggleActive = (competitionId: number, isActive: boolean) => {
    updateMutation.mutate({ id: competitionId, data: { is_active: !isActive } })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Unable to load competitions</h2>
        <p className="text-gray-600">Please try again later.</p>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Competition Management</h1>
            <p className="text-gray-600">Create and manage competitions for the Mahragan</p>
          </div>
          <button
            onClick={openCreateModal}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Competition
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrophyIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Competitions</dt>
                  <dd className="text-lg font-medium text-gray-900">{competitions.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-6 w-6 bg-green-500 rounded-full"></div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {competitions.filter((c: Competition) => c.is_active).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserIcon className="h-6 w-6 text-blue-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Individual</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {competitions.filter((c: Competition) => c.type === 'individual').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-6 w-6 text-purple-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Team</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {competitions.filter((c: Competition) => c.type === 'team').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <input
              type="text"
              placeholder="Search competitions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Categories</option>
              {Object.entries(categoryNames).map(([key, name]) => (
                <option key={key} value={key}>{name}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="individual">Individual</option>
              <option value="team">Team</option>
            </select>
          </div>
        </div>
      </div>

      {/* Competitions Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Competition
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category & Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Age Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <CalendarIcon className="h-4 w-4" />
                    <span>Schedule</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Participants
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCompetitions.map((competition: Competition) => {
                const IconComponent = categoryIcons[competition.category]
                return (
                  <tr key={competition.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <IconComponent className="h-6 w-6 text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{competition.name}</div>
                          {competition.description && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {competition.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <div className="font-medium">{categoryNames[competition.category]}</div>
                        <div className="text-xs">
                          {competition.type === 'individual' ? 'Individual' : 'Team'}
                          {competition.sport_type && ` • ${sportTypeNames[competition.sport_type]}`}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {competition.age_group_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <div>Reg: {new Date(competition.registration_deadline).toLocaleDateString()}</div>
                        <div>Event: {new Date(competition.start_date).toLocaleDateString()}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          competition.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {competition.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {competition.registered_count || 0}
                      {competition.max_participants && ` / ${competition.max_participants}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => toggleActive(competition.id, competition.is_active)}
                          className={`px-3 py-1 rounded text-xs font-medium ${
                            competition.is_active
                              ? 'bg-red-100 text-red-800 hover:bg-red-200'
                              : 'bg-green-100 text-green-800 hover:bg-green-200'
                          }`}
                        >
                          {competition.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => openEditModal(competition)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(competition.id, competition.name)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty State */}
      {filteredCompetitions.length === 0 && (
        <div className="text-center py-12">
          <TrophyIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No competitions found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterCategory !== 'all' || filterType !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by creating your first competition.'
            }
          </p>
          {!searchTerm && filterCategory === 'all' && filterType === 'all' && (
            <div className="mt-6">
              <button
                onClick={openCreateModal}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Create First Competition
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-8 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingCompetition ? 'Edit Competition' : 'Create New Competition'}
              </h3>
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Competition Name *
                    </label>
                    <input
                      {...register('name')}
                      type="text"
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      {...register('description')}
                      rows={3}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category *
                    </label>
                    <select
                      {...register('category')}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      {Object.entries(categoryNames).map(([key, name]) => (
                        <option key={key} value={key}>{name}</option>
                      ))}
                    </select>
                    {errors.category && (
                      <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type *
                    </label>
                    <select
                      {...register('type')}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="individual">Individual</option>
                      <option value="team">Team</option>
                    </select>
                    {errors.type && (
                      <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                    )}
                  </div>

                  {selectedCategory === 'sports' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sport Type
                      </label>
                      <select
                        {...register('sport_type')}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select sport...</option>
                        {Object.entries(sportTypeNames).map(([key, name]) => (
                          <option key={key} value={key}>{name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Age Group *
                    </label>
                    <select
                      {...register('age_group_id', { valueAsNumber: true })}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select age group...</option>
                      {ageGroups.map((group) => (
                        <option key={group.id} value={group.id}>{group.name}</option>
                      ))}
                    </select>
                    {errors.age_group_id && (
                      <p className="mt-1 text-sm text-red-600">{errors.age_group_id.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Participants
                    </label>
                    <input
                      {...register('max_participants', { valueAsNumber: true })}
                      type="number"
                      min="1"
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Registration Deadline *
                    </label>
                    <input
                      {...register('registration_deadline')}
                      type="date"
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                    {errors.registration_deadline && (
                      <p className="mt-1 text-sm text-red-600">{errors.registration_deadline.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date *
                    </label>
                    <input
                      {...register('start_date')}
                      type="date"
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                    {errors.start_date && (
                      <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date *
                    </label>
                    <input
                      {...register('end_date')}
                      type="date"
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                    {errors.end_date && (
                      <p className="mt-1 text-sm text-red-600">{errors.end_date.message}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <div className="flex items-center">
                      <input
                        {...register('is_active')}
                        id="is_active"
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                        Active (participants can register for this competition)
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false)
                      setEditingCompetition(null)
                      reset()
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createMutation.isLoading || updateMutation.isLoading ? (
                      <LoadingSpinner size="sm" />
                    ) : editingCompetition ? (
                      'Update Competition'
                    ) : (
                      'Create Competition'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CompetitionManagement